// Pure helpers shared by the explorer and search views: the contract/args tree
// model, name validation, and small string utilities. No React, no store wiring
// here, just data shaping so the views stay focused on rendering.

import { argsPathFor, type VFile } from "../store";

/** Split a path at its first dot so the extension can be rendered muted. */
export function splitExt(path: string): { base: string; ext: string } {
  const dot = path.indexOf(".");
  if (dot <= 0) return { base: path, ext: "" };
  return { base: path.slice(0, dot), ext: path.slice(dot) };
}

const NAME_RE = /^[A-Za-z0-9_./-]+$/;

/** Validate a proposed file path against the current set. Returns an error or null. */
export function validateName(
  raw: string,
  files: Record<string, VFile>,
  ignore?: string,
): string | null {
  const name = raw.trim();
  if (!name) return "Name cannot be empty.";
  if (name.startsWith(".")) return "Name cannot start with a dot.";
  if (!NAME_RE.test(name)) return "Use letters, digits, . _ - / only.";
  if (name !== ignore && files[name]) return `“${name}” already exists.`;
  return null;
}

/** Add a `.sl` extension to a bare contract name if none was supplied. */
export function withBsExt(raw: string): string {
  const name = raw.trim();
  return /\.[^./]+$/.test(name) ? name : `${name}.sl`;
}

/** A single path segment (no slashes), validated against siblings. `parent` is
 *  the containing folder ("" for root); `ignore` is the current name on rename. */
export function validateSegment(
  raw: string,
  parent: string,
  files: Record<string, VFile>,
  folders: string[],
  ignore?: string,
): string | null {
  const name = raw.trim();
  if (!name) return "Name cannot be empty.";
  if (name.startsWith(".")) return "Name cannot start with a dot.";
  if (name.includes("/")) return "Name cannot contain a slash.";
  if (!/^[A-Za-z0-9_.-]+$/.test(name)) return "Use letters, digits, . _ - only.";
  const full = parent ? `${parent}/${name}` : name;
  if (full !== ignore && (files[full] || folders.includes(full))) {
    return `“${name}” already exists.`;
  }
  return null;
}

export interface ContractGroup {
  contract: VFile;
  args: VFile | null;
}

export interface TreeFileNode {
  type: "file";
  file: VFile;
  /** The `.args.json` companion, shown nested under a `.sl` file. */
  args: VFile | null;
}
export interface TreeFolderNode {
  type: "folder";
  path: string;
  name: string;
  children: TreeNode[];
}
export type TreeNode = TreeFolderNode | TreeFileNode;

function parentOf(path: string): string {
  const i = path.lastIndexOf("/");
  return i < 0 ? "" : path.slice(0, i);
}

/** A real nested directory tree from the flat file map + explicit folder list.
 *  Folders sort first, then files; a `.sl` file's `.args.json` is nested under it
 *  and not shown separately. */
export function buildFileTree(files: Record<string, VFile>, folders: string[]): TreeNode[] {
  const claimed = new Set<string>();
  for (const f of Object.values(files)) {
    if (f.path.endsWith(".sl")) {
      const ap = argsPathFor(f.path);
      if (files[ap]) claimed.add(ap);
    }
  }

  const folderNodes = new Map<string, TreeFolderNode>();
  const ensureFolder = (p: string): TreeFolderNode => {
    let node = folderNodes.get(p);
    if (!node) {
      node = { type: "folder", path: p, name: p.slice(p.lastIndexOf("/") + 1), children: [] };
      folderNodes.set(p, node);
      const par = parentOf(p);
      if (par) ensureFolder(par);
    }
    return node;
  };
  for (const fp of folders) ensureFolder(fp);

  const roots: TreeNode[] = [];

  for (const f of Object.values(files)) {
    if (claimed.has(f.path)) continue;
    const args = f.path.endsWith(".sl") ? (files[argsPathFor(f.path)] ?? null) : null;
    const node: TreeFileNode = { type: "file", file: f, args };
    const par = parentOf(f.path);
    if (par) ensureFolder(par).children.push(node);
    else roots.push(node);
  }

  for (const node of folderNodes.values()) {
    const par = parentOf(node.path);
    if (par) folderNodes.get(par)!.children.push(node);
    else roots.push(node);
  }

  const sortLevel = (nodes: TreeNode[]) => {
    nodes.sort((a, b) => {
      if (a.type !== b.type) return a.type === "folder" ? -1 : 1;
      const an = a.type === "folder" ? a.name : a.file.path;
      const bn = b.type === "folder" ? b.name : b.file.path;
      return an.localeCompare(bn);
    });
    for (const n of nodes) if (n.type === "folder") sortLevel(n.children);
  };
  sortLevel(roots);
  return roots;
}

export interface TreeModel {
  contracts: ContractGroup[];
  others: VFile[];
}

/** Group contracts with their `.args.json` companions; everything else is "other". */
export function buildTree(files: Record<string, VFile>): TreeModel {
  const all = Object.values(files);
  const claimed = new Set<string>();
  const contracts: ContractGroup[] = [];

  for (const file of all) {
    if (!file.path.endsWith(".sl")) continue;
    const argsPath = argsPathFor(file.path);
    const args = files[argsPath] ?? null;
    if (args) claimed.add(args.path);
    contracts.push({ contract: file, args });
  }
  contracts.sort((a, b) => a.contract.path.localeCompare(b.contract.path));

  const others = all
    .filter((f) => !f.path.endsWith(".sl") && !claimed.has(f.path))
    .sort((a, b) => a.path.localeCompare(b.path));

  return { contracts, others };
}
